/**
 * Where a voice note or a note goes when nothing is behind it — New → Voice
 * note on a phone (Board 16 D), which has no stop. The *Attached to* search
 * of §9.2 (`GET /activity/attach?q=`) as a bottom sheet of 52 px rows; a pick
 * brings what the server says comes with it (`/activity/attach/suggest`), so
 * a job brings its place and that place's contact, exactly as every New
 * sheet does.
 */
import { useEffect, useState } from 'react'
import { activityRequest } from '../api.js'
import { Glyph, type GlyphName } from '../glyphs.js'
import { StopSheet } from '../stop/sheet.js'
import type { SaveChip } from './saving-to.js'

interface Hit { type: 'job' | 'contact' | 'property'; id: string; label: string; sub: string }
interface Answer { jobs: Hit[]; contacts: Hit[]; properties: Hit[] }
interface Chip { type: 'job' | 'contact' | 'property'; id: string; label: string }

const GROUPS: { key: keyof Answer; title: string; glyph: GlyphName }[] = [
  { key: 'jobs', title: 'Jobs', glyph: 'layers' },
  { key: 'contacts', title: 'Contacts', glyph: 'user' },
  { key: 'properties', title: 'Properties', glyph: 'pin' },
]

/** A job's chip is its number, as Board 15 draws it (*J-3318*). */
const chipLabel = (hit: Hit): string => (hit.type === 'job' ? hit.label.split(' · ')[0] ?? hit.label : hit.label)

export function AttachSheet({ onPick, onClose }: {
  onPick: (chips: SaveChip[]) => void
  onClose: () => void
}) {
  const [q, setQ] = useState('')
  const [answer, setAnswer] = useState<Answer | null>(null)

  useEffect(() => {
    let live = true
    const timer = window.setTimeout(() => {
      if (!q.trim()) { setAnswer(null); return }
      activityRequest<Answer>(`/activity/attach?q=${encodeURIComponent(q.trim())}`)
        .then((found) => { if (live) setAnswer(found) })
        .catch(() => { if (live) setAnswer({ jobs: [], contacts: [], properties: [] }) })
    }, 200)
    return () => { live = false; window.clearTimeout(timer) }
  }, [q])

  const pick = async (hit: Hit) => {
    const picked: SaveChip = { type: hit.type, id: hit.id, label: chipLabel(hit) }
    const more = await activityRequest<{ items: Chip[] }>(`/activity/attach/suggest?type=${hit.type}&id=${hit.id}`)
      .then((found) => found.items).catch(() => [] as Chip[])
    onPick([picked, ...more.filter((chip) => chip.id !== hit.id).map((chip) => ({ ...chip }))])
  }

  const none = answer && GROUPS.every((group) => answer[group.key].length === 0)

  return (
    <StopSheet title="Attach to" sub="a job, a contact or a place" onClose={onClose}>
      <label className="flex h-11 items-center gap-2 rounded-[10px] border border-line bg-surface px-3 focus-within:border-accent">
        <span className="text-text-faint"><Glyph name="search" size={16} /></span>
        <input
          autoFocus
          value={q}
          onChange={(event) => setQ(event.target.value)}
          placeholder="Search jobs, contacts, places…"
          className="min-w-0 flex-1 bg-transparent text-[14.5px] outline-none focus-visible:shadow-none"
        />
      </label>
      {none && <p className="mt-3 text-[13px] text-text-muted">Nothing matches that.</p>}
      {answer && GROUPS.map((group) => answer[group.key].length > 0 && (
        <div key={group.key} className="mt-3">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[.06em] text-text-faint">{group.title}</div>
          {answer[group.key].map((hit) => (
            <button key={hit.id} type="button" onClick={() => void pick(hit)}
              className="flex h-[52px] w-full items-center gap-3 border-b border-line-faint text-left">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-line-soft text-text-muted">
                <Glyph name={group.glyph} size={15} />
              </span>
              <span className="flex min-w-0 flex-col">
                <b className="truncate text-[14px]">{hit.label}</b>
                {hit.sub && <span className="truncate text-[12px] text-text-muted">{hit.sub}</span>}
              </span>
            </button>
          ))}
        </div>
      ))}
    </StopSheet>
  )
}
